import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getApiUrl } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import { checkPermission } from '../utils/permissions';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';
import AccessDenied from './AccessDenied';
import LoadingSpinner from './LoadingSpinner';
import Toast from './Toast';

interface Expense {
  id: number;
  expense_date: string;
  subcategory_name: string;
  category_name: string;
  amount: number;
  description?: string;
  payment_method: 'cash' | 'bank_transfer' | 'upi';
  receipt_number?: string;
  notes?: string;
  created_by_name?: string;
  created_at: string;
  expense_type: 'inventory' | 'manual';
  inventory_id?: string;
  inventory_display_id?: string;
}

interface ExpenseCategory {
  id: number;
  name: string;
  description?: string;
  subcategories: ExpenseSubcategory[];
}

interface ExpenseSubcategory {
  id: number;
  category_id: number;
  name: string;
  description?: string;
}

const ExpenseManagement: React.FC = () => {
  const { user, token } = useAuth();
  const { selectedBranchId, selectedBranchName, isAllBranches } = useBranch();

  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [expensesData, setExpensesData] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0
  });
  const [summary, setSummary] = useState({
    totalAmount: 0,
    shopExpenses: 0
  });

  // Balance data state
  const [balanceData, setBalanceData] = useState({
    morning_balance: 0,
    current_balance: 0,
    date: ''
  });

  // Modal state for adding expenses
  const [isAddExpenseModalOpen, setIsAddExpenseModalOpen] = useState(false);
  const [isEditExpenseModalOpen, setIsEditExpenseModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [editSelectedCategory, setEditSelectedCategory] = useState<number | null>(null);
  const [editSelectedSubcategory, setEditSelectedSubcategory] = useState<number | null>(null);

  // Toast state
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
    isVisible: boolean;
  }>({
    message: '',
    type: 'info',
    isVisible: false
  });

  // Dropdown states
  const [dateRange, setDateRange] = useState('This Month');
  const [isDateRangeDropdownOpen, setIsDateRangeDropdownOpen] = useState(false);

  // Form state for adding expense
  const [expenseForm, setExpenseForm] = useState({
    expense_date: new Date().toISOString().split('T')[0],
    subcategory_id: '',
    amount: '',
    description: '',
    payment_method: 'cash' as 'cash' | 'bank_transfer' | 'upi',
    receipt_number: '',
    notes: ''
  });

  // Form state for editing expense
  const [editExpenseForm, setEditExpenseForm] = useState({
    expense_date: '',
    subcategory_id: '',
    amount: '',
    description: '',
    payment_method: 'cash' as 'cash' | 'bank_transfer' | 'upi',
    receipt_number: '',
    notes: ''
  });

  // Toast helper functions
  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning') => {
    setToast({
      message,
      type,
      isVisible: true
    });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  // Remove debounced search - now using search button only
  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     setDebouncedSearchTerm(searchTerm);
  //   }, 500); // 500ms debounce delay

  //   return () => clearTimeout(timer);
  // }, [searchTerm]);

  // Use refs to access current values without triggering useCallback dependencies
  const searchTermRef = useRef(searchTerm);
  const paginationRef = useRef(pagination);
  const dateRangeRef = useRef(dateRange);
  
  // Dropdown refs for outside click handling
  const dateRangeDropdownRef = useRef<HTMLDivElement>(null);

  // Update refs when values change
  useEffect(() => { searchTermRef.current = searchTerm; }, [searchTerm]);
  useEffect(() => { paginationRef.current = pagination; }, [pagination]);
  useEffect(() => { dateRangeRef.current = dateRange; }, [dateRange]);

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dateRangeDropdownRef.current && !dateRangeDropdownRef.current.contains(event.target as Node)) {
        setIsDateRangeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch categories for expense form
  const fetchCategories = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(getApiUrl('expenses/categories'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Filter out "Inventory Related" categories for manual expenses
          const filteredCategories = (result.categories || []).filter(
            (category: ExpenseCategory) => category.name !== 'Inventory Related'
          );
          setCategories(filteredCategories);
        }
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, [token]);

  // Fetch balance data
  const fetchBalance = useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch(getApiUrl('dashboard/balance'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setBalanceData(result.balance);
        }
      }
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  }, [token]);

  // Fetch expenses from database with server-side pagination
  const fetchExpenses = useCallback(async (page = 1, isPagination = false) => {
    if (!token) return;
    
    if (isPagination) {
      setPaginationLoading(true);
    } else {
      setLoading(true);
    }
    setError(null);
    
    try {
      // Build query parameters using current ref values
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', paginationRef.current.limit.toString());
      
      // Add search term if provided
      if (searchTermRef.current.trim()) {
        params.append('search', searchTermRef.current.trim());
      }
      
      // Add date range filter
      if (dateRangeRef.current) {
        params.append('date_range', dateRangeRef.current.toLowerCase().replace(' ', '_'));
      }
      
      // Always sort by date desc (recent first) for expenses
      params.append('sort', 'date_desc');

      // Add branch filter - only include expenses from specific branch if not "All Branches"
      if (!isAllBranches && selectedBranchId) {
        params.append('branch_id', selectedBranchId);
      }

      const response = await fetch(getApiUrl(`expenses?${params.toString()}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.success) {
          setExpensesData(result.expenses || []);
          setPagination({
            page: result.pagination?.page || page,
            limit: result.pagination?.limit || paginationRef.current.limit,
            total: result.pagination?.total || 0,
            totalPages: result.pagination?.totalPages || 0
          });
          // Update summary from API response
          if (result.summary) {
            setSummary({
              totalAmount: result.summary.totalAmount || 0,
              shopExpenses: result.summary.shopExpenses || 0
            });
          }
        } else {
          setError('Failed to fetch expenses');
          showToast('Failed to fetch expenses', 'error');
        }
      } else {
        setError('Failed to fetch expenses');
        showToast('Failed to fetch expenses', 'error');
      }
    } catch (error) {
      setError('Error fetching expenses');
      showToast('Error fetching expenses', 'error');
    } finally {
      setLoading(false);
      setPaginationLoading(false);
    }
  }, [token, selectedBranchId, isAllBranches]);

  // Load expenses when component mounts or when dependencies change
  useEffect(() => {
    if (!token) return;

    fetchExpenses(1);
    fetchCategories();
    fetchBalance();
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [token, selectedBranchId, isAllBranches, isInitialLoad, fetchExpenses, fetchCategories, fetchBalance]);

  // Trigger search when date range changes
  useEffect(() => {
    if (!token || isInitialLoad) return;
    fetchExpenses(1);
  }, [dateRange, token, isInitialLoad, fetchExpenses]);

  // Handle adding new expense
  const handleAddExpense = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!expenseForm.subcategory_id || !expenseForm.amount || !expenseForm.description) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    try {
      const response = await fetch(getApiUrl('expenses'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...expenseForm,
          amount: parseFloat(expenseForm.amount),
          subcategory_id: parseInt(expenseForm.subcategory_id)
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          showToast('Expense added successfully', 'success');
          setIsAddExpenseModalOpen(false);
          setExpenseForm({
            expense_date: new Date().toISOString().split('T')[0],
            subcategory_id: '',
            amount: '',
            description: '',
            payment_method: 'cash',
            receipt_number: '',
            notes: ''
          });
          setSelectedCategory(null);
          fetchExpenses(1);
        } else {
          showToast(result.message || 'Failed to add expense', 'error');
        }
      } else {
        showToast('Failed to add expense', 'error');
      }
    } catch (error) {
      showToast('Error adding expense', 'error');
    }
  };

  const handleEditExpense = (expense: Expense) => {
    setEditingExpense(expense);
    
    // Format date to YYYY-MM-DD for input field
    const formattedDate = expense.expense_date.split('T')[0];
    
    setEditExpenseForm({
      expense_date: formattedDate,
      subcategory_id: '', // Will be set when categories load
      amount: expense.amount.toString(),
      description: expense.description || '',
      payment_method: expense.payment_method,
      receipt_number: expense.receipt_number || '',
      notes: expense.notes || ''
    });
    
    // Find and set the category and subcategory
    const category = categories.find(cat => 
      cat.subcategories.some(sub => sub.name === expense.subcategory_name)
    );
    if (category) {
      setEditSelectedCategory(category.id);
      const subcategory = category.subcategories.find(sub => sub.name === expense.subcategory_name);
      if (subcategory) {
        setEditSelectedSubcategory(subcategory.id);
        setEditExpenseForm(prev => ({...prev, subcategory_id: subcategory.id.toString()}));
      }
    }
    
    setIsEditExpenseModalOpen(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editExpenseForm.subcategory_id || !editExpenseForm.amount || !editExpenseForm.description) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    if (!token || !editingExpense) return;

    try {
      const response = await fetch(getApiUrl(`expenses/${editingExpense.id}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editExpenseForm),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          showToast('Expense updated successfully', 'success');
          setIsEditExpenseModalOpen(false);
          setEditingExpense(null);
          setEditSelectedCategory(null);
          setEditSelectedSubcategory(null);
          setEditExpenseForm({
            expense_date: '',
            subcategory_id: '',
            amount: '',
            description: '',
            payment_method: 'cash',
            receipt_number: '',
            notes: ''
          });
          fetchExpenses(pagination.page);
        } else {
          showToast(result.message || 'Failed to update expense', 'error');
        }
      } else {
        showToast('Failed to update expense', 'error');
      }
    } catch (error) {
      showToast('Error updating expense', 'error');
    }
  };

  const handleDeleteExpense = async (expense: Expense) => {
    if (!token) return;

    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete this expense?\n\nAmount: ${formatCurrency(expense.amount)}\nDescription: ${expense.description}`)) {
      return;
    }

    try {
      const response = await fetch(getApiUrl(`expenses/${expense.id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          showToast('Expense deleted successfully', 'success');
          fetchExpenses(pagination.page);
        } else {
          showToast(result.message || 'Failed to delete expense', 'error');
        }
      } else {
        showToast('Failed to delete expense', 'error');
      }
    } catch (error) {
      showToast('Error deleting expense', 'error');
    }
  };

  // Check permissions
  if (!checkPermission(user?.role, 'expenses')) {
    return <AccessDenied resource="Expense Management" />;
  }

  // Date range options
  const dateRangeOptions = [
    'This Month',
    'Last Month', 
    'This Year',
    'Last Year'
  ];

  // Use summary from API for accurate totals across all expenses (not just current page)
  const totalAmount = summary.totalAmount;
  const totalShopAmount = summary.shopExpenses;

  // Use server-side filtered and sorted data directly
  const currentData = expensesData;

  // Get subcategories for selected category
  const getSubcategories = () => {
    if (!selectedCategory) return [];
    const category = categories.find(cat => cat.id === selectedCategory);
    return category?.subcategories || [];
  };

  // Icons
  const SearchIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );

  const FilterIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
    </svg>
  );

  const PlusIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );

  return (
    <>
      {/* Toast Notification */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
      
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Expense Management</h1>
            <p className="text-gray-500 mt-1">
              Track and manage business expenses including inventory costs
              {!isAllBranches && selectedBranchId ? (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                  📍 Filtering: {selectedBranchName}
                </span>
              ) : (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  🌐 Showing: All Branches
                </span>
              )}
            </p>
          </div>
          <div className="mt-4 lg:mt-0">
            <button
              onClick={() => setIsAddExpenseModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
            >
              <PlusIcon />
              Add Expense
            </button>
          </div>
        </div>

        {/* Branch Filter Info */}
        {!isAllBranches && selectedBranchId && user?.role !== 'branch_manager' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  Branch Filter Active
                </h3>
                <div className="mt-1 text-sm text-blue-700">
                  Showing only expenses from <strong>{selectedBranchName}</strong>. 
                  Select "All Branches" to see expenses from all locations.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Opening Balance</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(balanceData.morning_balance)}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Current Balance</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(balanceData.current_balance)}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Expenses</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalAmount)}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Shop Expenses</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalShopAmount)}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Search - Left side */}
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SearchIcon />
                </div>
                <input
                  type="text"
                  placeholder="Description, category, or receipt..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      searchTermRef.current = searchTerm;
                      fetchExpenses(1, true);
                    }
                  }}
                  className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                  style={{ width: '460px' }}
                />
              </div>
              <button
                onClick={() => {
                  // Use current searchTerm for search
                  searchTermRef.current = searchTerm;
                  fetchExpenses(1, true);
                }}
                disabled={paginationLoading}
                className="px-4 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors h-10 whitespace-nowrap flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {paginationLoading ? 'Searching...' : 'Search'}
              </button>
            </div>

            {/* Filters - Right side */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Date Range Dropdown */}
              <div className="relative" ref={dateRangeDropdownRef}>
                <button 
                  onClick={() => setIsDateRangeDropdownOpen(!isDateRangeDropdownOpen)}
                  className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white hover:border-gray-300 transition-colors h-10"
                >
                  <span className="text-sm font-medium text-gray-700">{dateRange}</span>
                  <svg className={`w-4 h-4 text-gray-400 transition-transform ${isDateRangeDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {isDateRangeDropdownOpen && (
                  <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    {dateRangeOptions.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setDateRange(option);
                          setIsDateRangeDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                          dateRange === option ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button 
                onClick={() => {
                  setSearchTerm('');
                  setDateRange('This Month');
                  setIsDateRangeDropdownOpen(false);
                }}
                className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:text-gray-900 transition-colors border border-gray-200 rounded-lg hover:border-gray-300 bg-white h-10"
              >
                <FilterIcon />
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Expense Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Method</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receipt</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-8 text-center">
                      <LoadingSpinner />
                      <p className="text-gray-500 mt-2">Loading expenses...</p>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-8 text-center text-red-500">
                      {error}
                    </td>
                  </tr>
                ) : currentData.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                      No expenses found
                    </td>
                  </tr>
                ) : (
                  currentData.map((expense) => (
                    <tr key={expense.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(expense.expense_date).toLocaleDateString('en-IN')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{expense.category_name}</div>
                        <div className="text-sm text-gray-500">{expense.subcategory_name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate" title={expense.description}>
                          {expense.description || 'N/A'}
                        </div>
                        {expense.inventory_display_id && (
                          <div className="text-xs text-blue-600 mt-1">
                            Inventory: {expense.inventory_display_id}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(expense.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          expense.payment_method === 'cash' 
                            ? 'bg-green-100 text-green-800'
                            : expense.payment_method === 'bank_transfer'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {expense.payment_method === 'bank_transfer' ? 'Bank Transfer' : 
                           expense.payment_method === 'upi' ? 'UPI' : 'Cash'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          expense.expense_type === 'inventory' 
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {expense.expense_type === 'inventory' ? 'Inventory' : 'Manual'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {expense.receipt_number || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {expense.expense_type === 'manual' && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditExpense(expense)}
                              className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded transition-colors"
                              title="Edit expense"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteExpense(expense)}
                              className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded transition-colors"
                              title="Delete expense"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        )}
                        {expense.expense_type === 'inventory' && (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center gap-2 mb-4 sm:mb-0">
              <span className="text-sm text-gray-700">Show</span>
              <select
                value={pagination.limit}
                onChange={(e) => {
                  setPagination(prev => ({ ...prev, limit: Number(e.target.value) }));
                  fetchExpenses(1, true);
                }}
                className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-700">
                entries (Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total})
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchExpenses(Math.max(1, pagination.page - 1), true)}
                disabled={pagination.page <= 1 || paginationLoading}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              {/* Page Numbers */}
              {[...Array(Math.min(pagination.totalPages, 5))].map((_, index) => {
                const page = pagination.page <= 3 ? index + 1 : pagination.page - 2 + index;
                if (page > pagination.totalPages) return null;
                
                return (
                  <button
                    key={page}
                    onClick={() => fetchExpenses(page, true)}
                    disabled={paginationLoading}
                    className={`px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed ${
                      page === pagination.page
                        ? 'bg-primary-500 text-white border-primary-500'
                        : 'border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              
              <button
                onClick={() => fetchExpenses(Math.min(pagination.totalPages, pagination.page + 1), true)}
                disabled={pagination.page >= pagination.totalPages || paginationLoading}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add Expense Modal */}
      {isAddExpenseModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Add New Expense</h2>
                <button
                  onClick={() => setIsAddExpenseModalOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleAddExpense} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={expenseForm.expense_date}
                    onChange={(e) => setExpenseForm(prev => ({ ...prev, expense_date: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedCategory || ''}
                    onChange={(e) => {
                      const categoryId = parseInt(e.target.value);
                      setSelectedCategory(categoryId);
                      setExpenseForm(prev => ({ ...prev, subcategory_id: '' }));
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedCategory && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subcategory <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={expenseForm.subcategory_id}
                      onChange={(e) => {
                        setExpenseForm(prev => ({ ...prev, subcategory_id: e.target.value }));
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    >
                      <option value="">Select Subcategory</option>
                      {getSubcategories().map(subcategory => (
                        <option key={subcategory.id} value={subcategory.id}>
                          {subcategory.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={expenseForm.amount}
                    onChange={(e) => {
                      // Only allow numeric input with decimal
                      const value = e.target.value;
                      if (value === '' || /^\d*\.?\d*$/.test(value)) {
                        setExpenseForm(prev => ({ ...prev, amount: value }));
                      }
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="0.00"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={expenseForm.description}
                    onChange={(e) => setExpenseForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    rows={3}
                    placeholder="Enter expense description"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Method <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={expenseForm.payment_method}
                    onChange={(e) => setExpenseForm(prev => ({ ...prev, payment_method: e.target.value as 'cash' | 'bank_transfer' | 'upi' }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  >
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="upi">UPI</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Receipt Number
                  </label>
                  <input
                    type="text"
                    value={expenseForm.receipt_number}
                    onChange={(e) => setExpenseForm(prev => ({ ...prev, receipt_number: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter receipt number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={expenseForm.notes}
                    onChange={(e) => setExpenseForm(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    rows={2}
                    placeholder="Additional notes (optional)"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsAddExpenseModalOpen(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                  >
                    Add Expense
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Expense Modal */}
      {isEditExpenseModalOpen && editingExpense && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Edit Expense</h2>
                <button
                  onClick={() => {
                    setIsEditExpenseModalOpen(false);
                    setEditingExpense(null);
                    setEditSelectedCategory(null);
                    setEditSelectedSubcategory(null);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleEditSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={editExpenseForm.expense_date}
                    onChange={(e) => setEditExpenseForm(prev => ({ ...prev, expense_date: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={editSelectedCategory || ''}
                    onChange={(e) => {
                      const categoryId = parseInt(e.target.value);
                      setEditSelectedCategory(categoryId);
                      setEditSelectedSubcategory(null);
                      setEditExpenseForm(prev => ({ ...prev, subcategory_id: '' }));
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                {editSelectedCategory && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subcategory <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={editSelectedSubcategory || ''}
                      onChange={(e) => {
                        const subcategoryId = parseInt(e.target.value);
                        setEditSelectedSubcategory(subcategoryId);
                        setEditExpenseForm(prev => ({ ...prev, subcategory_id: e.target.value }));
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    >
                      <option value="">Select Subcategory</option>
                      {categories
                        .find(cat => cat.id === editSelectedCategory)
                        ?.subcategories.map((subcategory) => (
                          <option key={subcategory.id} value={subcategory.id}>
                            {subcategory.name}
                          </option>
                        ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editExpenseForm.amount}
                    onChange={(e) => {
                      // Only allow numeric input with decimal
                      const value = e.target.value;
                      if (value === '' || /^\d*\.?\d*$/.test(value)) {
                        setEditExpenseForm(prev => ({ ...prev, amount: value }));
                      }
                    }}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter amount"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editExpenseForm.description}
                    onChange={(e) => setEditExpenseForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter description"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Method
                  </label>
                  <select
                    value={editExpenseForm.payment_method}
                    onChange={(e) => setEditExpenseForm(prev => ({ ...prev, payment_method: e.target.value as 'cash' | 'bank_transfer' | 'upi' }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="upi">UPI</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Receipt Number
                  </label>
                  <input
                    type="text"
                    value={editExpenseForm.receipt_number}
                    onChange={(e) => setEditExpenseForm(prev => ({ ...prev, receipt_number: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter receipt number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={editExpenseForm.notes}
                    onChange={(e) => setEditExpenseForm(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    rows={2}
                    placeholder="Additional notes (optional)"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditExpenseModalOpen(false);
                      setEditingExpense(null);
                      setEditSelectedCategory(null);
                      setEditSelectedSubcategory(null);
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                  >
                    Update Expense
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExpenseManagement;